import { Board } from "../board";
import { Direction, cycleDirection, reverseDirection } from "../direction";
import { Mob } from "../mob";
import { MusicId } from "../musicId";
import { isPassableThroughDoors } from "../passability";
import { randomChance, randomElement, randomInt, seededRng, weightedRandom } from "../random";
import { Strings } from "../strings";
import { XY } from "../xy";
import { BigSquidTentacleCorpse } from "../mobs/bigSquidTentacleCorpse";
import { Biter } from "../mobs/biter";
import { BossPlowRobot } from "../mobs/bossPlowRobot";
import { CaveRobot } from "../mobs/caveRobot";
import { FloatingRobot } from "../mobs/floatingRobot";
import { Horned } from "../mobs/horned";
import { HornedCorpse } from "../mobs/hornedCorpse";
import { Slugant } from "../mobs/slugant";
import { ToxicSlug } from "../mobs/toxicSlug";
import { SlugantCorpse } from "../mobs/slugantCorpse";
import { Squid } from "../mobs/squid";
import { AlbinoSquid } from "../mobs/albinoSquid";
import { SquidCorpse } from "../mobs/squidCorpse";
import { BOARD_WIDTH, BOARD_HEIGHT } from "../gameConfig";
import { BlastDoor } from "../things/blastDoor";
import { BloodPool } from "../things/bloodPool";
import { Downstairs } from "../things/downstairs";
import { Fire } from "../things/fire";
import { FireTower } from "../things/fireTower";
import { JetpackLootBox } from "../things/jetpackLootBox";
import { LadderUp } from "../things/ladderUp";
import { Lamp } from "../things/lamp";
import { LevelDown } from "../things/levelDown";
import { LevelUp } from "../things/levelUp";
import { NightVisionLootBox } from "../things/nightVisionLootBox";
import { OpenBlastDoorsTerminal } from "../things/openBlastDoorsTerminal";
import { PowerPlantGateOnArrival } from "../things/powerPlantGateOnArrival";
import { PowerPlantKeyCardLootBox } from "../things/powerPlantKeyCardLootBox";
import { PowerPlantLightsOnArrival } from "../things/powerPlantLightsOnArrival";
import { PowerPlantSwitch } from "../things/powerPlantSwitch";
import { SlidingDoor } from "../things/slidingDoor";
import { TerminalState } from "../things/terminal";
import { Upstairs } from "../things/upstairs";
import { WoodenDoor } from "../things/woodenDoor";
import { MobSpawnerConfig } from "../things/portal2/mobSpawnerConfig";
import { PowerPlantKeyCard } from "../items/powerPlantKeyCard";
import { generateBranch3xbLevel } from "./branch3xbLevelGenerator";
import { generateCaveLevel, type CaveLevelParams } from "./caveLevelGenerator";
import {
	applySimplexNoise,
	assignGoldToPrePlacedMobs,
	breadthFirstSearch,
	getMultiTileThings,
	getRandomXYByCriteriaInArea,
	getSingleThingPrefab,
	placeRandomPortal,
	populateVendingMachines,
} from "./levelGeneratorUtils";
import { mobs, type MobClass } from "./mobGenerationConfig";
import { buildPrefab, buildPrefabWithOverrides } from "./prefabs/prefabBuilder";
import { powerPlantSwitchPrefabData } from "./prefabs/generalPrefabData";
import { miniBossRobotRoom, powerPlantLootRoom, vendingMachineData } from "./prefabs/robotBasePrefabData";
import { wastelandLevel4PrefabData } from "./prefabs/wastelandLevel4PrefabData";
import { getFinalBranchStairPrefab, getRobotBaseEntrancePrefab } from "./prefabs/otherPrefabs";
import { getExitTo3xbCave, getRobotFortress, getVendingMachineRoom } from "./prefabs/wastelandPrefabs";
import { ProdLootTable } from "./prodLootTable";
import { ProdVendingTables } from "./prodVendingTables";
import { generateRobotBaseLevel, type RobotBaseLevelParams } from "./robotBaseLevelGenerator";
import { generateRobotIntroLevel } from "./robotIntroLevelGenerator";
import { RoomType } from "./robotBaseRoom";
import { generateWastelandLevel, type WastelandLevelParams } from "./wastelandLevelGenerator";
import { BigContainer } from "../things/bigContainer.ts";
import { LootBox } from "../things/lootBox.ts";
import { MetalLootBox } from "../things/metalLootBox.ts";
import { MedicalLootBox } from "../things/medicalLootBox.ts";
import { Cannon } from "../weapons/cannon.ts";
import { CollectableItem } from "../collectableItem.ts";
import { Ammo } from "../items/ammo.ts";
import { ConcreteBlock } from "../things/concreteBlock.ts";
import { MetalBlock } from "../things/metalBlock.ts";
import { DestroyedRock } from "../things/destroyedRock.ts";
import { ScheduledHint } from "../things/scheduledHint.ts";
import { Hints } from "../hints/hints.ts";
import { getBigContainerPrefab } from "./prefabs/generalPrefabs.ts";
import { EngineerRobot } from "../mobs/engineerRobot.ts";
import { BlackHoleRobot } from "../mobs/blackHoleRobot.ts";

const LEVEL_WITH_EXIT_TO_BRANCH_3XB = 6;

export const WASTELAND_LEVEL_START = 1;
export const WASTELAND_LEVEL_END = 10;

export const WASTELAND_POWER_PLANT_LEVEL = 4;

export const CAVES_LEVEL_START = 11;
export const CAVES_LEVEL_END = 16;

export const ROBOT_INTRO_LEVEL = CAVES_LEVEL_END + 1;

export const ROBOT_LEVEL_START = ROBOT_INTRO_LEVEL + 1;
export const ROBOT_LEVEL_END = ROBOT_LEVEL_START + 4;

export const BRANCH_3XB_START = 10000;
export const BRANCH_3XB_END = 10100;

export const POWER_PLANT_LEVEL_1 = 1000;
export const POWER_PLANT_LEVEL_2 = 1001;
export const POWER_PLANT_CAVES_LEVEL_1 = 2000;
export const POWER_PLANT_CAVES_LEVEL_2 = 2001;
export const ROBOT_FINAL_BRANCH_LEVEL = 3000;

const ROBOT_LEVEL_WITH_EXIT_TO_FINAL_BRANCH = ROBOT_LEVEL_START + 3;

/** Main descent only — branch and power-plant side areas (1000+) don't count as depth progress. */
export function isMainPathLevel(levelNumber: number): boolean {
	return levelNumber >= WASTELAND_LEVEL_START && levelNumber <= ROBOT_LEVEL_END;
}

const PLACEHOLDER_XY = new XY(0, 0);

function makeSpawnerConfig(entries: { mobType: string; share: number }[]): MobSpawnerConfig {
	const config = new MobSpawnerConfig();
	for (const entry of entries) config.addEntry(entry);
	return config;
}

export function generateProdLevel(levelNumber: number, rng: () => number = Math.random, seed: string = ""): Board {
	if (levelNumber === WASTELAND_POWER_PLANT_LEVEL) {
		return getHandcraftedWastelandLevel4(rng);
	}
	if (levelNumber >= WASTELAND_LEVEL_START && levelNumber <= WASTELAND_LEVEL_END) {
		return getWastelandLevel(levelNumber, rng, seed);
	}
	if (levelNumber >= CAVES_LEVEL_START && levelNumber <= CAVES_LEVEL_END) {
		return getCaveLevel(levelNumber, rng);
	}
	if (levelNumber === ROBOT_INTRO_LEVEL) {
		return getRobotIntroLevel(rng);
	}
	if (levelNumber >= ROBOT_LEVEL_START && levelNumber <= ROBOT_LEVEL_END) {
		return getRobotLevel(levelNumber, rng);
	}
	if (levelNumber === ROBOT_FINAL_BRANCH_LEVEL) {
		return getRobotFinalLevel(rng);
	}
	if (levelNumber >= BRANCH_3XB_START && levelNumber <= BRANCH_3XB_END) {
		return getBranch3xbLevel(levelNumber, rng);
	}
	if (levelNumber === POWER_PLANT_LEVEL_1 || levelNumber === POWER_PLANT_LEVEL_2) {
		return getPowerPlantLevel(levelNumber, rng);
	}
	if (levelNumber === POWER_PLANT_CAVES_LEVEL_1 || levelNumber === POWER_PLANT_CAVES_LEVEL_2) {
		return getPowerPlantCaveLevel(levelNumber, rng);
	}
	return getCursedLevel(levelNumber, rng);
}

// Wasteland arrival/exit sides, a pure function of (seed, level). See docs/level-generation-seeding.md.
function wastelandArrivalAndExit(seed: string, wlNum: number): { arrivalSide: Direction; exitSide: Direction } {
	const anchor = wlNum <= 3 ? 1 : 5; // level 4 is hand-crafted and breaks the chain
	let arrivalSide = Direction.E;
	let exitSide = wastelandExitSide(seed, arrivalSide, anchor);
	for (let i = anchor + 1; i <= wlNum; i++) {
		arrivalSide = reverseDirection(exitSide);
		exitSide = wastelandExitSide(seed, arrivalSide, i);
	}
	return { arrivalSide, exitSide };
}

function wastelandExitSide(seed: string, arrivalSide: Direction, wlNum: number): Direction {
	const rng = seededRng(`${seed}_warrival_${wlNum}`);
	return cycleDirection(arrivalSide, randomInt(1, 3, rng) * 2);
}

function getWastelandLevel(levelNumber: number, rng: () => number, seed: string): Board {
	const wastelandLevelNumber = levelNumber - WASTELAND_LEVEL_START + 1;

	const base: WastelandLevelParams = {
		levelNumber,
		rng,
		...wastelandArrivalAndExit(seed, wastelandLevelNumber),
		exitLevelNumber: levelNumber + 1,
		lootTable: ProdLootTable.getWastelandLootTable(),
		metalLootTable: ProdLootTable.getWastelandMetalLootTable(),
		rewardLevel: levelNumber,
		numberOfLootBoxes: randomInt(1, 3, rng),
		numberOfMetalLootBoxes: randomChance(0.6, rng) ? 1 : 0,
		numberOfMedikits: randomChance(0.6, rng) ? 1 : 0,
		// Keys (avg ~0.55/level — players accumulate keys faster than they spend them)
		numberOfKeys: weightedRandom(
			new Map([
				[0, 50],
				[1, 45],
				[2, 5],
			]),
			rng,
		),
		// Containers (avg 0.2/level — each container holds 4 inner loot boxes)
		numberOfBigContainers: weightedRandom(
			new Map([
				[0, 80],
				[1, 20],
			]),
			rng,
		),
		numberOfFlavourPrefabs: weightedRandom(
			new Map([
				[0, 30],
				[1, 50],
				[2, 20],
			]),
			rng,
		),
		requiredPrefabs: levelNumber === LEVEL_WITH_EXIT_TO_BRANCH_3XB ? [getExitTo3xbCave(BRANCH_3XB_START)] : [],
	};

	const overrides = wastelandLevelOverrides(wastelandLevelNumber, rng);
	if (!overrides) {
		// Placeholder for unconfigured wasteland levels
		const board = generateWastelandLevel(base);
		board.areaName =
			Strings.get("area_cursed_wasteland", wastelandLevelNumber) ?? "Cursed Wastelands " + wastelandLevelNumber;
		applyCursedDecorations(board, rng);
		return board;
	}

	const board = generateWastelandLevel({ ...base, ...overrides });
	board.areaName =
		Strings.get("area_wasteland_level", wastelandLevelNumber) ?? "Wastelands level " + wastelandLevelNumber;
	board.musicId = MusicId.WASTELAND;
	if (wastelandLevelNumber === 1) {
		board.addNonPhysicalThing(new ScheduledHint(Hints.HOW_TO_WAIT));
	} else if (wastelandLevelNumber === 2) {
		board.addNonPhysicalThing(new ScheduledHint(Hints.DESTROY_PORTALS));
	} else if (wastelandLevelNumber === 3) {
		board.addNonPhysicalThing(new ScheduledHint(Hints.AUTO_SHOOT));
	}
	return board;
}

function wastelandLevelOverrides(
	wastelandLevelNumber: number,
	rng: () => number,
): Partial<WastelandLevelParams> | null {
	switch (wastelandLevelNumber) {
		case 1:
			return {
				mobGenerationConfig: [mobs(Squid, 5, 10), mobs(Horned, 5, 10)],
				useLevelDirector: false,
				numberOfFlavourPrefabs: 0,
				numberOfMedikits: 0,
				numberOfMetalLootBoxes: 0,
				numberOfLootBoxes: 3,
				numberOfKeys: 0,
				numberOfBigContainers: 0,
			};
		case 2:
			return {
				mobGenerationConfig: [mobs(Squid, 7, 15), mobs(Slugant, 1, 5)],
				numberOfPortals: 1,
				mobSpawnerConfig: makeSpawnerConfig([{ mobType: Squid.key, share: 1 }]),
				useLevelDirector: false,
			};
		case 3:
			return {
				mobGenerationConfig: [mobs(Squid, 7, 15), mobs(Horned, 7, 15)],
				mobSpawnerConfig: makeSpawnerConfig([
					{ mobType: Squid.key, share: 3 },
					{ mobType: Horned.key, share: 1 },
				]),
				numberOfPortals: 2,
				numberOfKeys: 1,
				useLevelDirector: true,
			};
		case 5:
			return {
				mobGenerationConfig: [mobs(Squid, 20, 25)],
				numberOfPortals: 0,
				numberOfBigSquids: 2,
				useLevelDirector: true,
				levelDirectorOnlySpawnCaves: true,
			};
		case 6:
			return standardWasteland(rng);
		case 7:
			// Rest area 2
			return {
				numberOfPortals: 0,
				useLevelDirector: false,
				numberOfRoads: 1,
				numberOfFlavourPrefabs: 0,
				numberOfLootBoxes: 3,
				numberOfMetalLootBoxes: 2,
				requiredPrefabs: [getRobotFortress()],
			};
		case 8:
			return {
				mobGenerationConfig: [mobs(Squid, 18, 25), mobs(Horned, 10, 16)],
				numberOfPortals: 0,
				numberOfArtilleryRobots: 2,
				useLevelDirector: false,
				numberOfRoads: randomInt(2, 3, rng),
				numberOfFlavourPrefabs: 0,
			};
		case 9:
			return {
				...standardWasteland(rng),
				numberOfRoads: 1,
				requiredPrefabs: [getVendingMachineRoom()],
				vendingTable: ProdVendingTables.getLateWasteland(),
			};
		case 10:
			return {
				...standardWasteland(rng),
				generateExitToCaves: true,
				numberOfFlavourPrefabs: 0,
				numberOfRoads: 1,
			};
		default:
			return null;
	}
}

function standardWasteland(rng: () => number): Partial<WastelandLevelParams> {
	// One common mob (squid/horned), then either the other common one or a
	// heavy slug. Only one heavy slug is ever drawn, so slugant and toxic
	// slug never share a level.
	const categoryA: MobClass[] = [Squid, Horned];
	const firstMob = randomElement(categoryA, rng)!;
	const otherA = categoryA.find((m) => m !== firstMob)!;
	const secondMob = weightedRandom(
		new Map<MobClass, number>([
			[otherA, 2],
			[Slugant, 1],
			[ToxicSlug, 1],
		]),
		rng,
	);
	const secondMobIsHeavySlug = secondMob === Slugant || secondMob === ToxicSlug;

	return {
		numberOfBigSquids: randomInt(0, 2, rng),
		useLevelDirector: true,
		numberOfPortals: randomInt(2, 4, rng),
		reinforcedPortalChance: 0.2,
		mobGenerationConfig: [mobs(firstMob, 7, 15), mobs(secondMob, 7, 15)],
		...(secondMobIsHeavySlug ? { portalSpecialMob: { mobType: secondMob.key, chance: 0.1 } } : {}),
		mobSpawnerConfig: makeSpawnerConfig([
			{ mobType: Squid.key, share: 3 },
			{ mobType: Horned.key, share: 1 },
		]),
	};
}

function applyCursedDecorations(board: Board, rng: () => number, includeFireTowers: boolean = false): void {
	applySimplexNoise(board, 6, 0.7, () => new BloodPool(PLACEHOLDER_XY), rng);
	applySimplexNoise(board, 6, 0.9, () => new SquidCorpse(PLACEHOLDER_XY), rng);
	applySimplexNoise(board, 6, 0.9, () => new SlugantCorpse(PLACEHOLDER_XY), rng);
	applySimplexNoise(board, 6, 0.9, () => new HornedCorpse(PLACEHOLDER_XY), rng);
	applySimplexNoise(board, 6, 0.9, () => new BigSquidTentacleCorpse(PLACEHOLDER_XY), rng);
	applySimplexNoise(board, 6, 0.8, () => new Fire(PLACEHOLDER_XY), rng);
	if (includeFireTowers) {
		applySimplexNoise(board, 6, 0.8, () => new FireTower(PLACEHOLDER_XY), rng);
	}
}

function getCaveLevel(levelNumber: number, rng: () => number): Board {
	const caveLevelNumber = levelNumber - CAVES_LEVEL_START + 1;

	const base: CaveLevelParams = {
		levelNumber,
		rng,
		exitLevelNumber: levelNumber + 1,
		lootTable: ProdLootTable.getCaveLootTable(),
		metalLootTable: ProdLootTable.getCaveMetalLootTable(),
		rewardLevel: caveLevelNumber,
		numberOfLootBoxes: randomInt(2, 4, rng),
		numberOfMetalLootBoxes: 1,
		numberOfMedikits: randomChance(0.4, rng) ? 1 : 0,
		numberOfKeys: weightedRandom(
			new Map([
				[0, 20],
				[1, 65],
				[2, 10],
				[3, 5],
			]),
			rng,
		),
		numberOfBigContainers: weightedRandom(
			new Map([
				[0, 56],
				[1, 40],
				[2, 3],
				[3, 1],
			]),
			rng,
		),
		numberOfFlavourPrefabs: weightedRandom(
			new Map([
				[0, 30],
				[1, 50],
				[2, 20],
			]),
			rng,
		),
	};

	const overrides = caveLevelOverrides(caveLevelNumber, levelNumber, rng);
	if (!overrides) {
		// Placeholder for unconfigured cave levels
		const board = generateCaveLevel({
			...base,
			randomStartingWallPercentage: 0.5,
			requiredOpenAreaPercent: { min: 0.6, max: 0.8 },
			generateLavaPools: true,
			lavaFrequency: 3,
			lavaLimit: 0.6,
			generateRiver: false,
		});
		board.areaName = Strings.get("area_cursed_caves", caveLevelNumber) ?? "Cursed Caves " + caveLevelNumber;
		applyCursedDecorations(board, rng, true);
		return board;
	}

	const board = generateCaveLevel({ ...base, ...overrides });
	board.areaName = Strings.get("area_caves", caveLevelNumber) ?? "Caves level " + caveLevelNumber;
	board.musicId = MusicId.CAVES;
	return board;
}

function caveLevelOverrides(
	caveLevelNumber: number,
	levelNumber: number,
	rng: () => number,
): Partial<CaveLevelParams> | null {
	if (caveLevelNumber === 1) {
		return {
			mobGenerationConfig: [mobs(Biter, 5, 10)],
			numberOfBigSquids: randomInt(1, 2, rng),
			generateChasms: true,
			generateRiver: false,
			generateLavaPools: false,
			randomStartingWallPercentage: 0.45,
			requiredOpenAreaPercent: { min: 0.4, max: 0.7 },
			numberOfFlavourPrefabs: 0,
			numberOfMedikits: 0,
			numberOfMetalLootBoxes: 0,
			numberOfLootBoxes: 0,
			numberOfKeys: 0,
			numberOfBigContainers: 0,
			generateSpecialEntrance: true,
			requiredPrefabs: [getVendingMachineRoom()],
			vendingTable: ProdVendingTables.getEarlyCaves(),
		};
	}
	if (caveLevelNumber >= 2 && caveLevelNumber < 6) {
		return standardCaves(rng);
	}
	if (caveLevelNumber === 6) {
		// Last cave. Pre-robot-base resupply: more boxes (not richer ones) so
		// the extra loot is noticeable. Normal boxes doubled, metal boxes tripled.
		return {
			...standardCaves(rng),
			numberOfLootBoxes: randomInt(4, 8, rng),
			numberOfMetalLootBoxes: 3,
			generateRiver: false,
			generateChasms: false,
			generateLavaPools: false,
			randomStartingWallPercentage: 0.42,
			requiredOpenAreaPercent: { min: 0.7, max: 0.98 },
			numberOfFlavourPrefabs: 0,
			requiredPrefabs: [getRobotBaseEntrancePrefab(levelNumber + 1), getVendingMachineRoom()],
			vendingTable: ProdVendingTables.getLateCaves(),
			numberOfPortals: 4,
			reinforcedPortalChance: 0.7,
			generateExit: false,
		};
	}
	return null;
}

function standardCaves(rng: () => number): Partial<CaveLevelParams> {
	const caveVariation = randomInt(1, 4, rng);

	let terrain: Partial<CaveLevelParams>;
	if (caveVariation === 1) {
		// Lots of lava
		terrain = {
			generateChasms: false,
			generateRiver: false,
			generateLavaPools: true,
			lavaFrequency: 3,
			lavaLimit: 0.6,
			randomStartingWallPercentage: 0.37,
			requiredOpenAreaPercent: { min: 0.8, max: 0.9 },
		};
	} else if (caveVariation === 2) {
		// Chasms
		terrain = {
			generateChasms: true,
			generateRiver: false,
			generateLavaPools: false,
			chasmFrequency: 3,
			chasmLimit: 0.6,
			randomStartingWallPercentage: 0.48,
			requiredOpenAreaPercent: { min: 0.18, max: 0.6 },
		};
	} else if (caveVariation === 3) {
		// River with some lava
		terrain = {
			generateChasms: false,
			generateRiver: true,
			generateLavaPools: true,
			lavaFrequency: 0.6,
			lavaLimit: 0.8,
			requiredOpenAreaPercent: { min: 0.15, max: 0.8 },
		};
	} else {
		// Pure cave
		terrain = {
			generateChasms: false,
			generateRiver: false,
			generateLavaPools: false,
			requiredOpenAreaPercent: { min: 0.08, max: 0.6 },
		};
	}

	// Slot 1. Squid appearances are split 50/50 between normal and albino.
	const mobPool1 = new Map<MobClass, number>([
		[Squid, 0.25],
		[AlbinoSquid, 0.25],
		[Horned, 0.5],
	]);
	const mob1 = weightedRandom(mobPool1, rng);

	// Slot 2
	const mobPool2 = new Map<MobClass, number>([
		[Biter, 1],
		[CaveRobot, 1],
		[ToxicSlug, 1],
	]);
	if (terrain.generateChasms || terrain.generateLavaPools) {
		mobPool2.set(FloatingRobot, 1);
	} else {
		mobPool2.set(Slugant, 1);
	}
	const mob2 = weightedRandom(mobPool2, rng);

	// Slot 3
	const slot3Monster = randomInt(1, 3, rng);

	return {
		...terrain,
		numberOfPortals: randomInt(2, 4, rng),
		reinforcedPortalChance: 0.2,
		portalSpawnProbability: 0.16, // default is 0.12 — caves spawn faster
		mobGenerationConfig: [mobs(mob1, 7, 15), mobs(mob2, 7, 15)],
		mobSpawnerConfig: makeSpawnerConfig([{ mobType: mob1.key, share: 1 }]),
		...(slot3Monster === 1
			? { numberOfBigSquids: randomInt(1, 2, rng) }
			: slot3Monster === 2
				? { numberOfDrillerRobots: randomInt(1, 2, rng) }
				: { numberOfArtilleryRobots: randomInt(1, 2, rng) }),
	};
}

function getRobotIntroLevel(rng: () => number): Board {
	const board = generateRobotIntroLevel({
		levelNumber: ROBOT_INTRO_LEVEL,
		rng,
		lootTable: ProdLootTable.getRobotBaseLootTable(),
		metalLootTable: ProdLootTable.getRobotBaseMetalLootTable(),
		rewardLevel: 0,
	});
	board.areaName = Strings.get("area_base_level", 0) ?? "Base level 0";
	board.musicId = MusicId.ROBOT_BASE;
	return board;
}

function getRobotLevel(levelNumber: number, rng: () => number): Board {
	if (levelNumber === ROBOT_LEVEL_END) {
		return getRobotBossLevel(rng);
	}
	const robotLevelNumber = levelNumber - ROBOT_LEVEL_START + 1;

	const minMobs = 3;
	// Resupply shops mid-base: one guaranteed vending machine on levels 19 and 21.
	const hasVendingMachine = robotLevelNumber === 2 || robotLevelNumber === 4;
	const hasBranchExit = levelNumber === ROBOT_LEVEL_WITH_EXIT_TO_FINAL_BRANCH;

	const board = generateRobotBaseLevel({
		levelNumber,
		rng,
		rewardLevel: robotLevelNumber,
		lootTable: ProdLootTable.getRobotBaseLootTable(),
		metalLootTable: ProdLootTable.getRobotBaseMetalLootTable(),
		// The exit placer ignores prefab tiles and seals its room's border, so the
		// branch stair can be stacked on, deleted, or walled in — reject those boards.
		accept: (b) => {
			if (b.getAllThings().filter((t) => t instanceof Mob).length < minMobs) return false;
			if (!hasBranchExit) return true;
			const stairs = b.getPhysicalThings().filter((t): t is Downstairs => t instanceof Downstairs);
			const toNext = stairs.filter((s) => s.destinationLevel === levelNumber + 1);
			const toBranch = stairs.filter((s) => s.destinationLevel === ROBOT_FINAL_BRANCH_LEVEL);
			if (toNext.length !== 1 || toBranch.length !== 1) return false;
			if (toNext[0].xy.equals(toBranch[0].xy)) return false;
			return b.isPassable(toNext[0].xy) && b.isPassable(toBranch[0].xy);
		},
		requiredPrefabs: [
			{
				prefab: getSingleThingPrefab(() => new EngineerRobot(new XY(0, 0)), true, false),
				canBePlacedInStarterRoom: false,
				canBePlacedInVaultRoom: true,
			},
			...(hasVendingMachine
				? [
						{
							prefab: buildPrefab(vendingMachineData),
							canBePlacedInStarterRoom: false,
							canBePlacedInVaultRoom: false,
						},
					]
				: []),
			...(hasBranchExit
				? [
						{
							prefab: getFinalBranchStairPrefab(ROBOT_FINAL_BRANCH_LEVEL),
							canBePlacedInStarterRoom: false,
							canBePlacedInVaultRoom: false,
						},
					]
				: []),
		],
		...(hasVendingMachine ? { vendingTable: ProdVendingTables.getRobotBase() } : {}),
	});
	board.areaName = Strings.get("area_base_level", robotLevelNumber) ?? "Base level " + robotLevelNumber;
	board.musicId = MusicId.ROBOT_BASE;
	return board;
}

function getRobotFinalLevel(rng: () => number): Board {
	const board = generateRobotBaseLevel({
		levelNumber: ROBOT_FINAL_BRANCH_LEVEL,
		rng,
		rewardLevel: 5,
		lootTable: ProdLootTable.getRobotBaseLootTable(),
		metalLootTable: ProdLootTable.getRobotBaseMetalLootTable(),
		previousLevelNumber: ROBOT_LEVEL_WITH_EXIT_TO_FINAL_BRANCH,
		placeEquation: true,
		minRoomSize: 9,
		maxRoomSize: 15,
		lockedExitProbability: 0,
		vaultRoomProbability: 0,
		corridorRoomProbability: 0.8,
		requiredDecorationPercentMin: 0.3,
		requiredDecorationPercentMax: 0.4,
		numberOfSpawners: 0,
		placeExit: false,
		availableRoomTypes: [RoomType.STUDY, RoomType.OFFICE],
		margin: 2,
	});
	board.areaName = Strings.get("area_base_archives") ?? "Base archives";
	board.musicId = MusicId.ROBOT_BASE;
	return board;
}

// Snapshot of the dev playground boss arena — deliberately not shared with
// PlaygroundLevelGenerator, which is a scratch testbed free to change.
function getRobotBossLevel(rng: () => number): Board {
	const board = generateRobotBaseLevel({
		levelNumber: ROBOT_LEVEL_END,
		rng,
		rewardLevel: 5,
		lootTable: ProdLootTable.getRobotBaseLootTable(),
		metalLootTable: ProdLootTable.getRobotBaseMetalLootTable(),
		numberOfSpawners: 0,
		maxMobs: 0,
		lockedExitProbability: 0,
		minRoomSize: 4,
		maxRoomSize: 10,
		vaultRoomProbability: 0.8,
		corridorRoomProbability: 0.3,
		requiredDecorationPercentMin: 0.6,
		requiredDecorationPercentMax: 0.9,
		maxOpeningRatio: 0,
		placeExit: false,
	});

	const entrance = board.getPhysicalThings().find((t) => t instanceof Upstairs);
	const startXY = entrance?.xy ?? new XY(5, 5);
	const reachable = breadthFirstSearch(startXY, (xy) => board.isInBounds(xy) && isPassableThroughDoors(board, xy));
	board.addThing(new BlackHoleRobot(reachable[reachable.length - 1]));
	assignGoldToPrePlacedMobs(board);

	board.areaName = Strings.get("area_base_level", 5) ?? "Base level 5";
	board.musicId = MusicId.ROBOT_BASE;
	return board;
}

function getBranch3xbLevel(levelNumber: number, rng: () => number): Board {
	const branchLevelNumber = levelNumber - BRANCH_3XB_START + 1;
	if (branchLevelNumber === 1) {
		// Entrance cave — procedural, no hazards, tight layout
		const board = generateCaveLevel({
			levelNumber,
			rng,
			numberOfPortals: 0,
			generateChasms: false,
			generateRiver: false,
			generateLavaPools: false,
			requiredOpenAreaPercent: { min: 0.08, max: 0.4 },
			previousLevelNumber: LEVEL_WITH_EXIT_TO_BRANCH_3XB,
			exitLevelNumber: levelNumber + 1,
		});
		board.areaName = Strings.get("area_branch_cave") ?? "Branch Cave";
		board.musicId = MusicId.CAVES;
		return board;
	} else if (branchLevelNumber === 2) {
		// Hand-crafted loot cave
		const board = generateBranch3xbLevel(levelNumber, rng);
		board.areaName = Strings.get("area_branch_cave") ?? "Branch Cave";
		board.musicId = MusicId.CAVES;
		return board;
	}
	return getCursedLevel(levelNumber, rng);
}

function getCursedLevel(levelNumber: number, rng: () => number): Board {
	const board = generateWastelandLevel({ levelNumber, rng });
	board.areaName = Strings.get("area_cursed_wasteland", levelNumber) ?? "Cursed Wastelands " + levelNumber;
	applyCursedDecorations(board, rng);
	return board;
}

function getHandcraftedWastelandLevel4(rng: () => number): Board {
	const board = new Board(BOARD_WIDTH, BOARD_HEIGHT);
	board.defaultFloor = "BrownFloor";
	board.areaName = Strings.get("area_wasteland_level", 4) ?? "Wastelands level 4";
	board.musicId = MusicId.WASTELAND;

	const prefab = buildPrefabWithOverrides(wastelandLevel4PrefabData, {
		U: { class: Upstairs.key, props: { destination: POWER_PLANT_LEVEL_1 } },
		H: { class: "LadderDown", props: { destination: POWER_PLANT_CAVES_LEVEL_1 } },
		J: { class: "LadderDown", props: { destination: POWER_PLANT_CAVES_LEVEL_2 } },
	});
	prefab.addToBoard(board, new XY(0, 0));

	board.addNonPhysicalThing(new PowerPlantGateOnArrival());

	// The prefab starts with the blast-door terminals unpowered; they come online
	// once the player activates the power plant (see PowerPlantGateOnArrival).
	for (const t of board.getPhysicalThings()) {
		if (t instanceof OpenBlastDoorsTerminal) {
			t.setState(TerminalState.NO_POWER);
		}
	}

	// board.addThing(new PowerPlantKeyCardLootBox(new XY(28, 14), [new PowerPlantKeyCard(1)]));
	// board.addThing(new PowerPlantSwitch(new XY(27, 14)));
	// board.addThing(new NightVisionLootBox(new XY(18, 17)));
	// board.addThing(new JetpackLootBox(new XY(19, 17)));

	// Entry from wasteland 3 and exit to wasteland 5 — not part of the prefab because
	// the prefab is shared with the dev playground (which lacks those neighbors).
	board.addThing(new LevelUp(new XY(2, 22), WASTELAND_POWER_PLANT_LEVEL - 1));
	board.addThing(new LevelDown(new XY(45, 16), WASTELAND_POWER_PLANT_LEVEL + 1));

	populateVendingMachines(board, ProdVendingTables.getEarlyGame(), rng);

	// bigcontainers full of cannon bullets
	const containers = getMultiTileThings(board, BigContainer);
	for (const container of containers) {
		while (container.getLoot().length < 4) {
			let lootBox = new LootBox(new XY(0, 0));
			lootBox.addLoot(new Ammo(Cannon.key, 10));
			container.addLoot(lootBox);
		}
	}

	// spawner and enemies area
	const enemyArea = breadthFirstSearch(new XY(0, 10), (xy) => {
		if (!board.isPassable(xy)) return false;
		return !board
			.getNeighbors(xy)
			.flatMap((xy) => board.getThingsAt(xy))
			.some((t) => t instanceof ConcreteBlock || t instanceof MetalBlock);
	}).filter((xy) => xy.x > 10);

	const numberOfPortalsToSpawn = 3;
	const numberOfMobsToSpawn = randomInt(2, 4, rng);

	for (let i = 0; i < numberOfPortalsToSpawn; i++) {
		const portal = placeRandomPortal(enemyArea, board, rng, false);
		if (!portal) continue;
		portal.addConfig({ mobType: Squid.key, share: 1 });
	}

	for (let i = 0; i < numberOfMobsToSpawn; i++) {
		const xy = getRandomXYByCriteriaInArea(enemyArea, (xy) => board.isPassable(xy) && !board.hasMob(xy), rng);
		if (xy == null) break;
		board.addThing(new Squid(xy));
	}

	applySimplexNoise(
		board,
		6,
		0.8,
		() => new DestroyedRock(new XY(0, 0)),
		rng,
		(xy) => board.isCellEmpty(xy),
	);

	assignGoldToPrePlacedMobs(board);
	board.dynamicGoldBudget = 300;

	return board;
}

function getPowerPlantLevel(levelNumber: number, rng: () => number): Board {
	const powerPlantIndex = levelNumber === POWER_PLANT_LEVEL_1 ? 1 : 2;
	const base: RobotBaseLevelParams = {
		levelNumber,
		rng,
		rewardLevel: powerPlantIndex,
		lootTable: ProdLootTable.getPowerPlantLootTable(),
		metalLootTable: ProdLootTable.getPowerPlantMetalLootTable(),
		lockedExitProbability: 0,
		vaultRoomProbability: 0,
		corridorRoomProbability: 0.5,
		requiredDecorationPercentMin: 0.8,
		requiredDecorationPercentMax: 1,
		numberOfSpawners: 0,
		openDoorProbability: 0,
		maxMobs: 200,
		maxOpeningRatio: 0,
		entranceSupplier: (xy, lvl) => new Downstairs(xy, lvl),
	};

	if (levelNumber === POWER_PLANT_LEVEL_1) {
		const board = generateRobotBaseLevel({
			...base,
			maxOpeningRatio: 0.2,
			minRoomSize: 5,
			maxRoomSize: 15,
			availableRoomTypes: [RoomType.POWERPLANT1],
			previousLevelNumber: WASTELAND_POWER_PLANT_LEVEL,
			exitSupplier: (xy, lvl) => new Upstairs(xy, lvl),
			exitLevelNumber: POWER_PLANT_LEVEL_2,
			placeExit: true,
			minExitDistance: 20,
			margin: 0,
			requiredPrefabs: [
				{
					prefab: getBigContainerPrefab(),
					canBePlacedInStarterRoom: false,
					allowedRoomTypes: [RoomType.POWERPLANT1],
				},
				{
					prefab: getBigContainerPrefab(),
					canBePlacedInStarterRoom: false,
					canBePlacedInVaultRoom: true,
					allowedRoomTypes: [RoomType.POWERPLANT1],
				},
			],
			accept: (b) => {
				const bigContainers = b.getPhysicalThings().filter((t) => t instanceof BigContainer);
				if (bigContainers.length < 1 || bigContainers.length > 2) return false;
				const downstairs = b.getPhysicalThings().filter((t) => t instanceof Downstairs);
				for (const bc of bigContainers) {
					for (const ds of downstairs) {
						if (bc.xy.distanceTo(ds.xy) < 20) return false;
					}
				}
				return true;
			},
		});
		board.initialSpawnXY = new XY(5, 5);
		board.darkness = 0.85;
		board.lightInnerRadius = 0.5;
		board.lightOuterRadius = 5;
		board.addNonPhysicalThing(new PowerPlantLightsOnArrival());
		board.areaName = Strings.get("area_power_plant_level", 1) ?? "Power plant level 1";

		const upstairs = board.getPhysicalThings().find((t) => t instanceof Upstairs);
		if (upstairs) board.addThing(new Lamp(upstairs.xy));
		board.musicId = MusicId.ROBOT_BASE;
		return board;
	}

	const board = generateRobotBaseLevel({
		...base,
		minRoomSize: 5,
		maxRoomSize: 13,
		availableRoomTypes: [RoomType.POWERPLANT_CONTROL_ROOM, RoomType.POWERPLANT2],
		previousLevelNumber: POWER_PLANT_LEVEL_1,
		placeExit: false,
		margin: 2,
		requiredPrefabs: [
			{
				prefab: buildPrefab(powerPlantSwitchPrefabData),
				canBePlacedInStarterRoom: false,
				canBePlacedInVaultRoom: false,
				allowedRoomTypes: [RoomType.POWERPLANT_CONTROL_ROOM],
			},
			{
				prefab: buildPrefab(miniBossRobotRoom),
				canBePlacedInStarterRoom: false,
				canBePlacedInVaultRoom: true,
				allowedRoomTypes: [RoomType.POWERPLANT2],
			},
			{
				prefab: buildPrefab(powerPlantLootRoom),
				canBePlacedInStarterRoom: false,
				canBePlacedInVaultRoom: true,
				allowedRoomTypes: [RoomType.POWERPLANT2],
			},
		],
		// Ensure the boss can reach the player once released. Player will spawn at the
		// Downstairs entrance, so use that as the proxy. Treat any door as passable since
		// the boss can break/open them once it starts moving. The jetpack loot room must
		// also be walkable-reachable — its SlidingDoor opens with the same switch group.
		accept: (b) => {
			const boss = b.getPhysicalThings().find((t) => t instanceof BossPlowRobot);
			const entrance = b.getPhysicalThings().find((t) => t instanceof Downstairs);
			const jetpack = b.getPhysicalThings().find((t) => t instanceof JetpackLootBox);
			if (!boss || !entrance || !jetpack) return false;
			const passableOrDoor = (xy: XY) => {
				if (!xy.isInside(b)) return false;
				if (b.isPassable(xy)) return true;
				return b
					.getThingsAt(xy)
					.some((t) => t instanceof BlastDoor || t instanceof WoodenDoor || t instanceof SlidingDoor);
			};
			const reachesEntrance = (from: XY) =>
				breadthFirstSearch(from, passableOrDoor).some((xy) => xy.equals(entrance.xy));
			return reachesEntrance(boss.xy) && reachesEntrance(jetpack.xy);
		},
	});
	board.darkness = 0.85;
	board.lightInnerRadius = 0.5;
	board.lightOuterRadius = 5;
	board.areaName = Strings.get("area_power_plant_level", 2) ?? "Power plant level 2";
	board.musicId = MusicId.ROBOT_BASE;
	return board;
}

function getPowerPlantCaveLevel(levelNumber: number, rng: () => number): Board {
	const undergroundIndex = levelNumber === POWER_PLANT_CAVES_LEVEL_1 ? 1 : 2;
	const base: CaveLevelParams = {
		levelNumber,
		rng,
		rewardLevel: undergroundIndex,
		lootTable: ProdLootTable.getPowerPlantUndergroundLootTable(),
		metalLootTable: ProdLootTable.getPowerPlantUndergroundMetalLootTable(),
		numberOfPortals: 0,
		generateChasms: false,
		generateRiver: false,
		generateSpecialEntrance: false,
		minExitDistance: 50,
		mobSpawnMinDistanceFromArrival: 17,
	};

	if (levelNumber === POWER_PLANT_CAVES_LEVEL_1) {
		const board = generateCaveLevel({
			...base,
			generateLavaPools: false,
			requiredOpenAreaPercent: { min: 0.1, max: 0.4 },
			randomStartingWallPercentage: 0.3,
			previousLevelNumber: WASTELAND_POWER_PLANT_LEVEL,
			exitLevelNumber: POWER_PLANT_CAVES_LEVEL_2,
			numberOfMetalLootBoxes: 1,
			numberOfLootBoxes: 2,
			mobGenerationConfig: [mobs(Horned, 20, 35)],
			mobSpawnerConfig: makeSpawnerConfig([{ mobType: Horned.key, share: 1 }]),
			numberOfPortals: 1,
		});
		board.areaName = Strings.get("area_power_plant_underground", 1) ?? "Power plant underground 1";
		board.darkness = 0.7;
		board.musicId = MusicId.CAVES;
		return board;
	}

	const board = generateCaveLevel({
		...base,
		generateLavaPools: true,
		lavaLimit: 0.3,
		requiredOpenAreaPercent: { min: 0.5, max: 0.9 },
		randomStartingWallPercentage: 0.4,
		previousLevelNumber: POWER_PLANT_CAVES_LEVEL_1,
		exitLevelNumber: WASTELAND_POWER_PLANT_LEVEL,
		exitSupplier: (xy, lvl) => new LadderUp(xy, lvl),
		numberOfMetalLootBoxes: 2,
		mobGenerationConfig: [mobs(FloatingRobot, 25, 35)],
	});
	board.areaName = Strings.get("area_power_plant_underground", 2) ?? "Power plant underground 2";
	board.darkness = 0;
	board.musicId = MusicId.CAVES;
	return board;
}
